import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { updateTrackStatus } from "../api";
import { UpdateStatusRequest, LeadTrackDTO } from "../dtos";
import { LEAD_TRACKS_QUERY_KEY } from "./use-lead-tracks.hook";
import { LEAD_TRACK_ACTIVITY_QUERY_KEY } from "./use-lead-track-activity.hook";
import { LEAD_TRACK_STATUSES_QUERY_KEY } from "./use-lead-track-statuses.hook";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";

interface UseUpdateTrackStatusMutationOptions {
  trackUId: string;
  onSuccess?: (data: LeadTrackDTO) => void;
  onError?: (error: unknown) => void;
}

export const useUpdateTrackStatusMutation = ({
  trackUId,
  onSuccess,
  onError,
}: UseUpdateTrackStatusMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<LeadTrackDTO>,
    { response?: { data?: { message?: string } } },
    UpdateStatusRequest
  >({
    mutationFn: (data) => updateTrackStatus(trackUId, data),
    onSuccess: (response) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: [LEAD_TRACKS_QUERY_KEY] });
      queryClient.invalidateQueries({ 
        queryKey: [LEAD_TRACK_ACTIVITY_QUERY_KEY, trackUId] 
      });
      // Invalidate track statuses (they change based on stage)
      queryClient.invalidateQueries({ queryKey: [LEAD_TRACK_STATUSES_QUERY_KEY] });
      // Invalidate lead data to reflect updated track information
      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: ["lead-activity"] });

      showToast({
        type: ToastType.SUCCESS,
        message: "Status updated successfully",
        toastId: TOAST_IDS.UPDATE_TRACK_STATUS_SUCCESS || "update-track-status-success",
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });

      onSuccess?.(response.data);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || "Failed to update status";
      
      showToast({
        type: ToastType.ERROR,
        message: errorMessage,
        toastId: TOAST_IDS.UPDATE_TRACK_STATUS_ERROR || "update-track-status-error",
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });

      onError?.(error);
    },
  });
};

export default useUpdateTrackStatusMutation;

